import { Component } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Radio, Tv } from "lucide-react";

// Class-based ErrorBoundary — React still requires a class for componentDidCatch.
// Wraps a subtree, catches render/runtime errors, and shows a friendly recovery
// screen instead of a blank / black page.
export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("RouteErrorBoundary caught:", error, info);
  }
  handleReload = () => {
    // Clear the error and try to re-render the same route once.
    this.setState({ error: null });
    // Force a soft reload of the current page's data.
    if (typeof window !== "undefined") window.location.reload();
  };
  render() {
    if (!this.state.error) return this.props.children;
    const message = this.state.error?.message || "A intervenit o eroare neașteptată.";
    return (
      <div
        data-testid="route-error-boundary"
        className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[#05070a]"
      >
        <div
          className="relative w-full max-w-lg rounded-2xl border-4 border-[#ff2fa7] bg-[#0a0f0d]/90 p-8 backdrop-blur-sm text-center"
          style={{ boxShadow: "0 0 24px rgba(255,47,167,0.55), 0 0 60px rgba(0,229,255,0.2)" }}
        >
          {/* Retro warning triangle */}
          <div className="flex justify-center mb-4">
            <div
              className="w-14 h-14 rounded-full border-4 border-[#ff2fa7] bg-[#0d1410] flex items-center justify-center animate-flicker"
              style={{ boxShadow: "0 0 18px rgba(255,47,167,0.9)" }}
            >
              <AlertTriangle className="w-7 h-7 text-[#ff2fa7]" strokeWidth={3} />
            </div>
          </div>

          <div className="text-[#00e5ff] text-[10px] font-black uppercase tracking-[0.3em] mb-2">
            Semnal întrerupt
          </div>
          <h1
            className="font-display text-3xl sm:text-4xl font-black text-white leading-tight mb-2"
            style={{ textShadow: "0 0 8px rgba(255,47,167,0.75)" }}
          >
            Ups! Ceva nu a mers bine
          </h1>
          <p className="text-slate-300 text-sm mb-1">
            Se pare că avem interferențe pe canal.
          </p>
          <p className="text-slate-500 text-xs italic mb-6 break-words px-2">
            {message}
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/grila-tv"
              data-testid="route-error-tv-guide-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#ff2fa7] text-white border-2 border-[#ff2fa7] font-black uppercase tracking-widest text-xs hover:scale-105 active:scale-95 transition-transform"
              style={{ boxShadow: "0 0 16px rgba(255,47,167,0.7)" }}
              onClick={() => this.setState({ error: null })}
            >
              <Radio className="w-4 h-4" strokeWidth={3} /> Mergi la Grila TV Live
            </Link>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="route-error-reload-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-transparent text-[#00e5ff] border-2 border-[#00e5ff] font-black uppercase tracking-widest text-xs hover:bg-[#00e5ff]/10"
            >
              <Tv className="w-4 h-4" strokeWidth={3} /> Reîncarcă pagina
            </button>
          </div>

          <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-slate-500">
            © Desene 2000 · Sistem de recuperare automată
          </div>
        </div>
      </div>
    );
  }
}
